/**
 * metaLeadGen/individualCreativeBreakdown.ts
 *
 * Per-ad funnel metrics, benchmark comparisons, drop-off analysis,
 * performance classification, and decision recommendations.
 *
 * ARCHITECTURE CONTRACT:
 *   - Pure data layer. No UI imports.
 *   - Same CRM mock rates as creativeBreakdown.ts — replace when Close CRM
 *     exposes ad-level dimensions.
 *   - Opt-in Rate = optins / clicks (per spec, not / LPV).
 *   - "Top N" ads ranked by optins desc, then impressions desc as tiebreaker.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters, CreativeType } from "./types";
import { classifyCreativeType } from "./creativeType";
import { applyFilters } from "./filters";
import { safeDivide } from "./calculations";
import { CREATIVE_COLORS, CREATIVE_LABELS } from "./creativeBreakdown";
import type { CloseCreativeMap } from "./useCloseCreativeAttribution";
import type { OptinAdMap } from "./useOptinAdAttribution";

// ─────────────────────────────────────────────
// MOCK CRM RATES — fallback when Close data unavailable
// ─────────────────────────────────────────────
const MOCK_APP_RATES: Record<CreativeType, number> = {
  "Video":        0.092,
  "Static Image": 0.078,
  "Carousel":     0.065,
  "UGC":          0.105,
  "Other":        0.055,
};

const MOCK_CLOSE_RATES: Record<CreativeType, number> = {
  "Video":        0.245,
  "Static Image": 0.220,
  "Carousel":     0.195,
  "UGC":          0.262,
  "Other":        0.180,
};

const MOCK_DEAL_VALUE = 2_000; // USD

// ─────────────────────────────────────────────
// OUTPUT TYPES
// ─────────────────────────────────────────────

/** Minimal descriptor for the ad selector dropdown */
export interface AdOption {
  adId:      string;
  adName:    string;
  shortName: string; // ≤ 40 chars, truncated with ellipsis
  type:      CreativeType;
  color:     string;
}

/** Full funnel row for a single ad */
export interface IndividualCreativeFunnelRow {
  adId:         string;
  adName:       string;
  shortName:    string;
  type:         CreativeType;
  typeLabel:    string;  // e.g. "Influencer / UGC"
  color:        string;

  // Volume
  spend:        number;
  impressions:  number;
  clicks:       number;
  optins:       number;
  applications: number;
  closed:       number;
  revenue:      number;

  // Rates (%)
  ctr:          number;
  optinRate:    number;
  appRate:      number;
  closeRate:    number;

  // Cost efficiency
  cpm:            number;   // spend / impressions * 1000
  costPerOptin:   number;
  costPerApp:     number;
  roas:           number;   // revenue / spend

  // Video metrics
  videoViews:     number;   // 3-second video view count (0 for non-video)
  videoViewRate:  number;   // videoViews / impressions * 100 (%)

  // Creative media
  thumbnailUrl?:  string;   // thumbnail/preview image URL if available
}

/** Benchmark for comparison against a single creative */
export interface BenchmarkRow {
  label:        string;
  ctr:          number;
  optinRate:    number;
  appRate:      number;
  closeRate:    number;
  costPerOptin: number;
  costPerApp:   number;
}

/** Which stage has the most drop-off */
export interface DropOffInfo {
  fromLabel: string;
  toLabel:   string;
  dropPct:   number;   // % of users that did NOT proceed (0–100)
  isWorst:   boolean;
  isSecond:  boolean;
}

export type PerformanceClass =
  | "Strong Performer"
  | "Clickbait Risk"
  | "High Intent, Weak Hook"
  | "Hook Works, Funnel Leaks"
  | "Low Volume"
  | "Needs Work";

export type Decision = "Scale" | "Fix" | "Kill";

export interface DecisionInfo {
  verdict: Decision;
  reason:  string;
}

/** Daily data point for the mini trend chart */
export interface CreativeDailyPoint {
  date:    string; // display format e.g. "Jan 13"
  optins:  number;
  apps:    number;
  clicks:  number;
}

/** Full analysis object returned for a single ad */
export interface IndividualCreativeAnalysis {
  funnel:      IndividualCreativeFunnelRow;
  accountAvg:  BenchmarkRow;
  typeAvg:     BenchmarkRow;
  dropOffs:    DropOffInfo[];
  classification: PerformanceClass;
  decision:    DecisionInfo;
  dailySeries: CreativeDailyPoint[];
}

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────

function shortName(adName: string): string {
  // Strip the "UU | " prefix and abbreviate
  const clean = adName.replace(/^[A-Z]+\s*\|\s*/i, "").trim();
  return clean.length > 42 ? clean.slice(0, 40) + "…" : clean;
}

interface RawAgg {
  spend: number;
  impressions: number;
  clicks: number;
  optins: number;
  videoViews: number;
}

function emptyAgg(): RawAgg {
  return { spend: 0, impressions: 0, clicks: 0, optins: 0, videoViews: 0 };
}

/**
 * Look up real optin count for an ad from the optin attribution map.
 * Tries: 1) exact match on ad_id, 2) utm_content substring in ad_name.
 */
function lookupRealOptins(
  adId: string,
  adName: string,
  optinAdMap?: OptinAdMap,
): number | null {
  if (!optinAdMap || optinAdMap.size === 0) return null;

  // 1. Exact match on ad_id
  const byId = optinAdMap.get(adId.trim());
  if (byId !== undefined) return byId;

  // 2. Check if any utm_content key matches within the ad_name
  const adNameLower = adName.toLowerCase().trim();
  for (const [utmContent, count] of optinAdMap) {
    const utmLower = utmContent.toLowerCase();
    if (utmLower.length < 3) continue;
    if (adNameLower.includes(utmLower)) return count;
  }

  return null;
}

function buildFunnelRow(
  adId: string,
  adName: string,
  type: CreativeType,
  agg: RawAgg,
  thumbnailUrl?: string,
  closeAttribution?: CloseCreativeMap,
  optinAdMap?: OptinAdMap,
): IndividualCreativeFunnelRow {
  // Use real optin count from MongoDB when available, otherwise fall back to Meta leads
  const realOptins = lookupRealOptins(adId, adName, optinAdMap);
  const optins = realOptins !== null ? realOptins : agg.optins;

  const closeRow = closeAttribution?.get(adName.toLowerCase().trim());
  const applications = closeRow && (closeRow.applications > 0 || closeRow.closed > 0)
    ? closeRow.applications
    : Math.round(optins * MOCK_APP_RATES[type]);
  const closed = closeRow && (closeRow.applications > 0 || closeRow.closed > 0)
    ? closeRow.closed
    : Math.round(applications * MOCK_CLOSE_RATES[type]);
  const revenue = closeRow && closeRow.revenue > 0
    ? closeRow.revenue
    : closed * MOCK_DEAL_VALUE;

  return {
    adId,
    adName,
    shortName: shortName(adName),
    type,
    typeLabel:      CREATIVE_LABELS[type],
    color:          CREATIVE_COLORS[type],
    spend:          agg.spend,
    impressions:    agg.impressions,
    clicks:         agg.clicks,
    optins,
    applications,
    closed,
    revenue,
    ctr:            safeDivide(agg.clicks,       agg.impressions)  * 100,
    optinRate:      safeDivide(optins,            agg.clicks)       * 100,
    appRate:        safeDivide(applications,      optins)            * 100,
    closeRate:      safeDivide(closed,            applications)     * 100,
    cpm:            safeDivide(agg.spend,         agg.impressions)  * 1000,
    costPerOptin:   safeDivide(agg.spend,         optins),
    costPerApp:     safeDivide(agg.spend,         applications),
    roas:           safeDivide(revenue,           agg.spend),
    videoViews:     agg.videoViews,
    videoViewRate:  safeDivide(agg.videoViews,    agg.impressions)  * 100,
    thumbnailUrl,
  };
}

function buildBenchmarkRow(label: string, rows: IndividualCreativeFunnelRow[]): BenchmarkRow {
  if (rows.length === 0) {
    return { label, ctr: 0, optinRate: 0, appRate: 0, closeRate: 0, costPerOptin: 0, costPerApp: 0 };
  }
  // Aggregate then recalculate — never average ratios
  const spend       = rows.reduce((s, r) => s + r.spend, 0);
  const impressions = rows.reduce((s, r) => s + r.impressions, 0);
  const clicks      = rows.reduce((s, r) => s + r.clicks, 0);
  const optins      = rows.reduce((s, r) => s + r.optins, 0);
  const applications= rows.reduce((s, r) => s + r.applications, 0);
  const closed      = rows.reduce((s, r) => s + r.closed, 0);

  return {
    label,
    ctr:          safeDivide(clicks, impressions)   * 100,
    optinRate:    safeDivide(optins, clicks)         * 100,
    appRate:      safeDivide(applications, optins)   * 100,
    closeRate:    safeDivide(closed, applications)   * 100,
    costPerOptin: safeDivide(spend, optins),
    costPerApp:   safeDivide(spend, applications),
  };
}

function computeDropOffs(row: IndividualCreativeFunnelRow): DropOffInfo[] {
  const transitions: Array<{ fromLabel: string; toLabel: string; from: number; to: number }> = [
    { fromLabel: "Impressions", toLabel: "Clicks",       from: row.impressions,  to: row.clicks },
    { fromLabel: "Clicks",      toLabel: "Opt-ins",      from: row.clicks,       to: row.optins },
    { fromLabel: "Opt-ins",     toLabel: "Applications", from: row.optins,       to: row.applications },
    { fromLabel: "Applications",toLabel: "Closed",       from: row.applications, to: row.closed },
  ];

  const withDrop = transitions.map(t => ({
    fromLabel: t.fromLabel,
    toLabel:   t.toLabel,
    dropPct:   t.from > 0 ? (1 - safeDivide(t.to, t.from)) * 100 : 0,
    isWorst:   false,
    isSecond:  false,
  }));

  // Mark worst and second-worst
  const sorted = [...withDrop].sort((a, b) => b.dropPct - a.dropPct);
  const worstEntry = withDrop.find(d => d.fromLabel === sorted[0]?.fromLabel);
  if (worstEntry) worstEntry.isWorst = true;

  const secondEntry = withDrop.find(d => !d.isWorst && d.fromLabel === sorted[1]?.fromLabel);
  if (secondEntry) secondEntry.isSecond = true;

  return withDrop;
}

function classifyPerformance(
  row: IndividualCreativeFunnelRow,
  accountAvg: BenchmarkRow
): PerformanceClass {
  if (row.impressions < 1000) return "Low Volume";

  const goodCtr      = row.ctr      >= accountAvg.ctr;
  const goodOptin    = row.optinRate >= accountAvg.optinRate;
  const goodApp      = row.appRate   >= accountAvg.appRate;

  if (goodCtr && goodOptin && goodApp)   return "Strong Performer";
  if (goodCtr && !goodOptin)             return "Clickbait Risk";
  if (!goodCtr && goodOptin && goodApp)  return "High Intent, Weak Hook";
  if (goodCtr && goodOptin && !goodApp)  return "Hook Works, Funnel Leaks";
  return "Needs Work";
}

function buildDecision(
  row: IndividualCreativeFunnelRow,
  classification: PerformanceClass,
  accountAvg: BenchmarkRow
): DecisionInfo {
  switch (classification) {
    case "Strong Performer":
      return { verdict: "Scale", reason: "Above-average CTR, opt-in rate, and app rate. Increase budget." };
    case "Clickbait Risk":
      return { verdict: "Fix", reason: "High CTR but poor opt-in rate — the hook attracts clicks that don't convert. Improve landing page alignment." };
    case "High Intent, Weak Hook":
      return { verdict: "Fix", reason: "Low CTR but high downstream conversion — the hook is weak. Test stronger opening lines." };
    case "Hook Works, Funnel Leaks":
      return { verdict: "Fix", reason: "Good top-of-funnel performance but drop-off after opt-in. Review follow-up sequence or qualification." };
    case "Low Volume":
      return { verdict: "Fix", reason: "Not enough data yet. Let spend accumulate before making decisions." };
    default: {
      // Needs Work — check if cost is way above avg
      const costBad = accountAvg.costPerOptin > 0 && row.costPerOptin > accountAvg.costPerOptin * 1.5;
      if (costBad) return { verdict: "Kill", reason: "Below-average rates and cost per opt-in is 50%+ above account average. Reallocate budget." };
      return { verdict: "Fix", reason: "Below-average performance across multiple stages. Audit the creative and targeting." };
    }
  }
}

function toDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Returns all unique ads in the filtered set, ranked by optins desc.
 * Used to populate the dropdown and the "Top 8" grid.
 */
export function getAdOptions(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters,
  optinAdMap?: OptinAdMap,
): AdOption[] {
  const filtered = applyFilters(rows, filters);

  const aggMap = new Map<string, { name: string; type: CreativeType; agg: RawAgg }>();

  for (const row of filtered) {
    if (!aggMap.has(row.adId)) {
      aggMap.set(row.adId, {
        name: row.adName,
        type: classifyCreativeType(row.adName),
        agg:  emptyAgg(),
      });
    }
    const entry = aggMap.get(row.adId)!;
    entry.agg.spend       += row.spend;
    entry.agg.impressions += row.impressions;
    entry.agg.clicks      += row.clicks;
    entry.agg.optins      += row.leads;
    entry.agg.videoViews  += row.videoViews ?? 0;
  }

  // Use real optin counts for sorting when available
  return Array.from(aggMap.entries())
    .sort((a, b) => {
      const aOptins = lookupRealOptins(a[0], a[1].name, optinAdMap) ?? a[1].agg.optins;
      const bOptins = lookupRealOptins(b[0], b[1].name, optinAdMap) ?? b[1].agg.optins;
      return bOptins - aOptins || b[1].agg.impressions - a[1].agg.impressions;
    })
    .map(([adId, { name, type }]) => ({
      adId,
      adName:    name,
      shortName: shortName(name),
      type,
      color:     CREATIVE_COLORS[type],
    }));
}

/**
 * Full analysis for a single ad.
 */
export function getIndividualCreativeAnalysis(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters,
  adId: string,
  closeAttribution?: CloseCreativeMap,
  optinAdMap?: OptinAdMap,
): IndividualCreativeAnalysis | null {
  const filtered = applyFilters(rows, filters);

  // Aggregate per-ad
  const aggMap = new Map<string, { name: string; type: CreativeType; agg: RawAgg; thumbnailUrl?: string }>();
  for (const row of filtered) {
    if (!aggMap.has(row.adId)) {
      aggMap.set(row.adId, {
        name: row.adName,
        type: classifyCreativeType(row.adName),
        agg:  emptyAgg(),
        thumbnailUrl: row.thumbnailUrl,
      });
    }
    const entry = aggMap.get(row.adId)!;
    entry.agg.spend       += row.spend;
    entry.agg.impressions += row.impressions;
    entry.agg.clicks      += row.clicks;
    entry.agg.optins      += row.leads;
    entry.agg.videoViews  += row.videoViews ?? 0;
    if (!entry.thumbnailUrl && row.thumbnailUrl) entry.thumbnailUrl = row.thumbnailUrl;
  }

  const target = aggMap.get(adId);
  if (!target) return null;

  const allFunnelRows = Array.from(aggMap.entries()).map(([id, { name, type, agg, thumbnailUrl }]) =>
    buildFunnelRow(id, name, type, agg, thumbnailUrl, closeAttribution, optinAdMap)
  );

  const funnel      = buildFunnelRow(adId, target.name, target.type, target.agg, target.thumbnailUrl, closeAttribution, optinAdMap);
  const accountAvg  = buildBenchmarkRow("Account Avg", allFunnelRows);
  const typeAvg     = buildBenchmarkRow(
    `${CREATIVE_LABELS[target.type]} Avg`,
    allFunnelRows.filter(r => r.type === target.type)
  );

  const dropOffs       = computeDropOffs(funnel);
  const classification = classifyPerformance(funnel, accountAvg);
  const decision       = buildDecision(funnel, classification, accountAvg);

  // Daily series for this specific ad
  const dateSet = new Set<string>();
  for (const row of filtered) if (row.adId === adId) dateSet.add(row.date);
  const dates = Array.from(dateSet).sort();

  const dailyAgg = new Map<string, RawAgg>();
  for (const date of dates) dailyAgg.set(date, emptyAgg());
  for (const row of filtered) {
    if (row.adId !== adId) continue;
    const d = dailyAgg.get(row.date)!;
    d.spend       += row.spend;
    d.impressions += row.impressions;
    d.clicks      += row.clicks;
    d.optins      += row.leads;
  }

  // Derive per-day app rate from real Close data if available, else mock
  const closeRow = closeAttribution?.get(target.name.toLowerCase().trim());
  const realAppRate = closeRow && closeRow.applications > 0 && funnel.optins > 0
    ? closeRow.applications / funnel.optins
    : MOCK_APP_RATES[target.type];

  const dailySeries: CreativeDailyPoint[] = dates.map(date => {
    const d = dailyAgg.get(date)!;
    // Use real optins for daily if available (proportional distribution)
    const totalMetaOptins = target.agg.optins;
    const realTotal = lookupRealOptins(adId, target.name, optinAdMap);
    const dayOptins = realTotal !== null && totalMetaOptins > 0
      ? Math.round((d.optins / totalMetaOptins) * realTotal)
      : d.optins;
    const apps = Math.round(dayOptins * realAppRate);
    return { date: toDisplayDate(date), optins: dayOptins, apps, clicks: d.clicks };
  });

  return { funnel, accountAvg, typeAvg, dropOffs, classification, decision, dailySeries };
}

/**
 * Returns funnel rows for the top N ads by optins.
 * Used by the "ALL TOP 8" toggle view.
 */
export function getTopNAdsData(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters,
  n: number = 8,
  closeAttribution?: CloseCreativeMap,
  optinAdMap?: OptinAdMap,
): IndividualCreativeFunnelRow[] {
  const filtered = applyFilters(rows, filters);

  const aggMap = new Map<string, { name: string; type: CreativeType; agg: RawAgg; thumbnailUrl?: string }>();
  for (const row of filtered) {
    if (!aggMap.has(row.adId)) {
      aggMap.set(row.adId, {
        name: row.adName,
        type: classifyCreativeType(row.adName),
        agg:  emptyAgg(),
        thumbnailUrl: row.thumbnailUrl,
      });
    }
    const entry = aggMap.get(row.adId)!;
    entry.agg.spend       += row.spend;
    entry.agg.impressions += row.impressions;
    entry.agg.clicks      += row.clicks;
    entry.agg.optins      += row.leads;
    entry.agg.videoViews  += row.videoViews ?? 0;
    if (!entry.thumbnailUrl && row.thumbnailUrl) entry.thumbnailUrl = row.thumbnailUrl;
  }

  return Array.from(aggMap.entries())
    .sort((a, b) => {
      const aOptins = lookupRealOptins(a[0], a[1].name, optinAdMap) ?? a[1].agg.optins;
      const bOptins = lookupRealOptins(b[0], b[1].name, optinAdMap) ?? b[1].agg.optins;
      return bOptins - aOptins || b[1].agg.impressions - a[1].agg.impressions;
    })
    .slice(0, n)
    .map(([id, { name, type, agg, thumbnailUrl }]) => buildFunnelRow(id, name, type, agg, thumbnailUrl, closeAttribution, optinAdMap));
}
